import { useEffect, useState } from "react";
import { Lock, ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { setReauthToken, clearReauthToken, isReauthValid, REAUTH_PAUSED_UNTIL_GOLIVE } from "../lib/reauth";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * ReauthGate — gates a children component behind a fresh password
 * (+ optional TOTP) check.
 *
 * Use case: Dealer settings should be re-authenticated even when the user is
 * already logged in, so that an open laptop in the shop is not a free pass for
 * any walking-by employee to change tax IDs, IBANs or social credentials.
 *
 * Once unlocked, a short-lived (15 min) reauth token is stored in
 * sessionStorage and forwarded as `X-Reauth-Token` by the api interceptor.
 */
export default function ReauthGate({ currentUser, children, label = "Einstellungen" }) {
    const totpEnabled = !!currentUser?.totp_enabled;
    const isSsoOnly = !!currentUser?.auth_provider && currentUser?.auth_provider === "google" && !currentUser?.password_hash_set;
    const paused = REAUTH_PAUSED_UNTIL_GOLIVE;
    const [unlocked, setUnlocked] = useState(paused || isReauthValid());
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [totpCode, setTotpCode] = useState("");
    const [busy, setBusy] = useState(false);

    // Re-check token validity periodically so the gate snaps shut once the
    // 15-minute window expires while the user is mid-edit. (Bei pausiertem
    // Gate bis zum Live-Gang nicht erneut sperren.)
    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => {
            if (!isReauthValid() && unlocked) {
                setUnlocked(false);
                toast.info(`${label} wurden zur Sicherheit wieder gesperrt — bitte erneut bestätigen.`);
            }
        }, 30 * 1000);
        return () => clearInterval(id);
    }, [paused, unlocked, label]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/auth/reauth", {
                password,
                totp_code: totpEnabled ? totpCode : null,
            });
            setReauthToken(data.reauth_token, data.expires_in_minutes || 15);
            setUnlocked(true);
            setPassword("");
            setTotpCode("");
            toast.success(`${label} entsperrt — gültig für ${data.expires_in_minutes || 15} Min.`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Re-Authentifizierung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const lockNow = () => {
        clearReauthToken();
        setUnlocked(false);
    };

    if (unlocked) {
        return (
            <div data-testid="reauth-gate-unlocked">
                {paused ? (
                    <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 px-4 py-2 rounded-sm text-xs" data-testid="reauth-paused-banner">
                        <ShieldCheck className="w-4 h-4 text-amber-700" />
                        <span className="text-amber-900 font-bold">
                            {label}: Passwort-Schutz ist bis zum Live-Gang vorübergehend deaktiviert.
                        </span>
                    </div>
                ) : (
                    <div className="mb-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-sm text-xs">
                        <ShieldCheck className="w-4 h-4 text-emerald-700" />
                        <span className="text-emerald-900 font-bold">
                            {label} entsperrt. {totpEnabled ? "2FA aktiv." : ""}
                        </span>
                        <button
                            onClick={lockNow}
                            className="ml-auto text-emerald-900 hover:underline font-semibold"
                            data-testid="reauth-lock-now"
                        >
                            Jetzt sperren
                        </button>
                    </div>
                )}
                {children}
            </div>
        );
    }

    if (isSsoOnly) {
        // Google-only accounts have no local password — without 2FA we can
        // still surface a gentle hint so the dealer knows why they cannot
        // re-auth via password.
        return (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-6" data-testid="reauth-gate-sso">
                <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-display text-lg font-bold mb-1">{label} sind geschützt</div>
                        <p className="text-sm text-amber-900">
                            Ihr Konto verwendet Google-Login ohne lokales Passwort. Bitte richten Sie zuerst ein Passwort
                            ein (Konto → Passwort) und aktivieren Sie 2FA, bevor Sie sensible Daten ändern.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8 max-w-lg" data-testid="reauth-gate">
            <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 bg-[#111827] flex items-center justify-center rounded-sm shrink-0">
                    <Lock className="w-5 h-5 text-[#FFCC00]" />
                </div>
                <div>
                    <div className="font-display text-xl font-black tracking-tight">{label} entsperren</div>
                    <p className="text-xs text-gray-600 mt-1">
                        Zur Sicherheit Ihrer Stammdaten bitten wir um eine zusätzliche Bestätigung —{" "}
                        {totpEnabled ? "Passwort + 2FA-Code" : "Ihr Passwort"}.
                    </p>
                </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Passwort</label>
                    <div className="relative">
                        <Input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoFocus
                            className="rounded-sm h-11 pr-11"
                            autoComplete="current-password"
                            data-testid="reauth-password-input"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                            aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                            data-testid="reauth-password-toggle"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
                {totpEnabled && (
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
                            <KeyRound className="w-3.5 h-3.5" /> 2FA-Code (Authenticator-App)
                        </label>
                        <Input
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="123456"
                            inputMode="numeric"
                            className="rounded-sm h-11 font-mono-num tracking-widest text-lg"
                            autoComplete="one-time-code"
                            data-testid="reauth-totp-input"
                            required
                        />
                        <p className="text-[10px] text-gray-500 mt-1">6-stelliger Code aus Ihrer Authenticator-App.</p>
                    </div>
                )}
                <Button
                    type="submit"
                    disabled={busy || !password || (totpEnabled && totpCode.length !== 6)}
                    className="w-full h-12 bg-[#111827] hover:bg-black text-white font-bold rounded-sm"
                    data-testid="reauth-submit-btn"
                >
                    {busy ? "Prüfe..." : `${label} entsperren`}
                </Button>
            </form>
        </div>
    );
}
